import { ArrowRight, CheckCircle2, ClipboardCheck, GraduationCap, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * A landing page, not a document ledger. Verified certificates, approved files,
 * historic checks and full form history are only browsed in My Records.
 * Keep genuinely actionable work and expiring certificates one tap away.
 */
const CERTIFICATE_ATTENTION = new Set(['expired', 'due_soon', 'rejected', 'needs_information']);

export function buildApplicantHomeModel({
  actions = [],
  trainings = [],
  expiredTrainings = [],
} = {}) {
  const actionable = (actions || []).filter((action) => (
    action?.classification === 'worker_action' && action?.actor !== 'admin'
  )).filter((action, index, list) => (
    list.findIndex((other) => (other.key || other.title) === (action.key || action.title)) === index
  ));
  const byKey = new Map();
  // Include the explicit expired list (which may not be in the training matrix),
  // then merge canonical training matrix rows. Never turn approved records into tasks.
  for (const item of [...(expiredTrainings || []), ...(trainings || [])]) {
    // The explicit expired collection can contain stale raw status values.
    const status = (expiredTrainings || []).includes(item)
      ? 'expired' : String(item?.status || '').toLowerCase();
    if (!CERTIFICATE_ATTENTION.has(status)) continue;
    const key = String(item?.id || item?.code || item?.name || '').trim();
    if (!key) continue;
    const current = byKey.get(key);
    if (!current || (status === 'expired' && current.status !== 'expired')) {
      byKey.set(key, { ...item, status, key });
    }
  }
  const priority = { expired: 0, rejected: 1, needs_information: 2, due_soon: 3 };
  const renewals = [...byKey.values()].sort((a, b) => (
    (priority[a.status] ?? 4) - (priority[b.status] ?? 4)
  ));
  return { actionable, renewals };
}

/**
 * Backend actions often repeat the task title as the button text
 * (e.g. "Upload DBS Certificate" twice). When that happens, show only the verb
 * on the button ("Upload") so the screen reads cleanly; the full wording stays
 * in the accessible name. Never changes which task opens.
 */
export function getApplicantHomeActionLabel(action) {
  const title = String(action?.title || '').trim();
  const label = String(action?.primary_label || '').trim();
  if (!label) return 'Continue';
  if (title && label.toLowerCase() === title.toLowerCase()) {
    const verb = label.split(/\s+/)[0];
    return verb && verb.length < label.length ? verb.charAt(0).toUpperCase() + verb.slice(1) : label;
  }
  return label;
}

// Older server copy still names the platform "CareOps". Applicants should see
// the platform name CareDock; tenant names are never hardcoded here.
export function toApplicantPlatformCopy(text) {
  return String(text || '').replace(/\bCareOps\b/g, 'CareDock');
}

const actionIdentity = (action) => String(action?.key || action?.title || '').toLowerCase();

export default function ApplicantHome({
  actions = [],
  primaryAction = null,
  trainings = [],
  expiredTrainings = [],
  uploading = false,
  uploadFeedback = null,
  onOpenTasks,
  onOpenTask,
  onRenewCertificate,
  onOpenInduction,
}) {
  const { actionable, renewals } = buildApplicantHomeModel({ actions, trainings, expiredTrainings });
  // `primaryAction` is the same server-backed next step My tasks shows (for
  // example the application gate or the wizard's forced task). Without it we
  // fall back to the first applicant-owned action, as before.
  const nextAction = primaryAction || actionable[0] || null;
  const otherActions = nextAction
    ? actionable.filter((action) => actionIdentity(action) !== actionIdentity(nextAction))
    : actionable;
  const remainingCount = otherActions.length;
  const hasWork = Boolean(nextAction) || renewals.length > 0;
  const showActionList = Boolean(nextAction) || renewals.length === 0;
  // The required next step is the main call to action. Induction can be done
  // at the same time, so it stays one tap away but is visually secondary
  // whenever the applicant has a task of their own to do.
  const inductionIsPrimary = !nextAction;

  return (
    <section className="space-y-4" aria-label="Application home" data-testid="applicant-home">
      <div className="px-1 pt-1" data-testid="applicant-home-intro">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#0F5C5E]">Your application</p>
        <h2 className="mt-1 font-heading text-xl font-bold text-slate-900">Welcome back</h2>
        <p className="mt-1 text-sm text-slate-600">
          {hasWork
            ? 'Here’s what needs your attention.'
            : onOpenInduction
              ? 'No other requests are listed right now. You can review induction below while your recruitment team completes any checks.'
              : 'No outstanding tasks are listed right now. Your recruitment team may still be reviewing your information.'}
        </p>
      </div>

      {uploadFeedback?.message && (
        <div
          role={uploadFeedback.status === 'error' ? 'alert' : 'status'}
          aria-live="polite"
          className={`rounded-xl border p-3 text-sm ${uploadFeedback.status === 'error' ? 'border-red-200 bg-red-50 text-red-900' : 'border-teal-200 bg-teal-50 text-teal-900'}`}
          data-testid="applicant-upload-feedback"
        >
          {uploadFeedback.message}
        </div>
      )}

      {renewals.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4 shadow-sm sm:p-5" data-testid="applicant-home-renewals">
          <h3 className="font-semibold text-amber-950">Certificates needing attention ({renewals.length})</h3>
          <p className="mt-1 text-sm text-amber-900">Renew or replace these directly. You don’t need to scroll past verified certificates.</p>
          <ul className="mt-3 space-y-2">
            {renewals.slice(0, 3).map((item) => {
              const name = item.name || item.training_name || 'Training certificate';
              return (
                <li key={item.key} className="flex flex-col gap-3 rounded-xl bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-sm font-semibold text-slate-900">{name}</p>
                    <p className="text-sm text-amber-800">{item.status === 'due_soon' ? 'Expiring soon' : item.status === 'expired' ? 'Expired' : 'Needs a replacement or more information'}</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => onRenewCertificate(item)}
                    disabled={Boolean(uploading)}
                    className="min-h-11 w-full sm:w-auto"
                    aria-label={`Renew ${name}`}
                    data-testid={`applicant-renew-${item.key}`}
                  >
                    <RefreshCw className="mr-1.5 h-4 w-4" /> Renew
                  </Button>
                </li>
              );
            })}
          </ul>
          {renewals.length > 3 && (
            <Button variant="link" className="mt-2 min-h-11 px-0" onClick={onOpenTasks}>
              View all {renewals.length} certificates <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {showActionList && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5" data-testid="applicant-home-action-list">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-semibold text-slate-900">{nextAction ? 'Your next step' : 'To do'}</h3>
            {nextAction ? (
              <Button variant="ghost" className="min-h-11 px-3" onClick={onOpenTasks}>View all <ArrowRight className="ml-1 h-4 w-4" /></Button>
            ) : null}
          </div>
          {!nextAction ? (
            <div className="mt-3 flex items-start gap-3 rounded-xl bg-emerald-50 p-3 text-sm text-emerald-900">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>No outstanding tasks are listed right now. Your recruitment team may still be checking your information.</span>
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              <div
                className="flex flex-col gap-4 rounded-xl border border-teal-100 bg-teal-50/40 p-4 sm:flex-row sm:items-center sm:justify-between"
                data-testid="applicant-home-next-step"
              >
                <div className="flex min-w-0 flex-1 items-start gap-3">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#0F5C5E] ring-1 ring-teal-100" aria-hidden="true">
                    <ClipboardCheck className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="break-words text-base font-semibold text-slate-900">{nextAction.title || 'Complete a task'}</p>
                    {nextAction.description && (
                      <p className="mt-1 line-clamp-3 break-words text-sm leading-6 text-slate-600" data-testid="applicant-home-next-step-description">
                        {toApplicantPlatformCopy(nextAction.description)}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  className="min-h-11 w-full whitespace-normal text-center sm:w-auto sm:shrink-0"
                  onClick={() => onOpenTask(nextAction)}
                  aria-label={nextAction.primary_label || (nextAction.title ? `Continue: ${nextAction.title}` : 'Continue')}
                  data-testid={`applicant-home-action-${nextAction.key}`}
                >
                  {getApplicantHomeActionLabel(nextAction)} <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
              {remainingCount > 0 && <p className="text-sm text-slate-600">{remainingCount} more in My tasks. You can do them in any order from View all.</p>}
            </div>
          )}
        </div>
      )}

      {onOpenInduction && (
        <div className="rounded-2xl border border-teal-200 bg-teal-50/60 p-4 sm:p-5" data-testid="applicant-home-induction">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-[#0F5C5E] ring-1 ring-teal-100" aria-hidden="true">
                <GraduationCap className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h3 className="font-semibold text-teal-950">Care Certificate induction</h3>
                <p className="mt-1 text-sm leading-6 text-teal-900">You can open or continue your induction here. You do not need to wait for your other documents to be checked.</p>
              </div>
            </div>
            <Button
              type="button"
              variant={inductionIsPrimary ? 'default' : 'outline'}
              className={`min-h-11 w-full sm:w-auto sm:shrink-0 ${inductionIsPrimary ? '' : 'bg-white'}`}
              onClick={onOpenInduction}
              data-testid="applicant-open-induction"
            >
              Open induction <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
          <p className="mt-3 text-xs leading-5 text-teal-900 sm:pl-[52px]">Official learning is provided where available. Your manager checks your answers, certificates and practical assessments. Final work-readiness approval is separate from completing induction.</p>
        </div>
      )}

    </section>
  );
}
